import { styled } from '@mui/material/styles';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import {
  Grid,
  GridProps,
  Card,
  CardProps,
  CardContent,
  Stack,
  Typography,
  TableRow,
  Box,
  createTheme,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import TableCell, {
  tableCellClasses,
  TableCellProps,
} from '@mui/material/TableCell';
import { ReactNode } from 'react';

// Extend MUI Typography to support custom variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    standardCopy: React.CSSProperties;
    largeLink: React.CSSProperties;
    standardLink: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    standardCopy?: React.CSSProperties;
    largeLink?: React.CSSProperties;
    standardLink?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    standardCopy: true;
    largeLink: true;
    standardLink: true;
  }
}

export const stringToColor = (str: string): string => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (let i = 0; i < 3; i++) {
    let value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color + '30';
};

/**
 * Convenience wrapper for an MUI Grid container that centralizes common layout tweaks.
 *
 * @param {React.ReactNode} children - Grid items to render.
 * @param {boolean} [noLeftMargin] - Removes the default negative left margin.
 * @param {boolean} [noTopMargin] - Removes the default top margin.
 * @param {Object} [props] - Additional Grid props forwarded to the container.
 */
interface GridContainerProps extends Omit<GridProps, 'container'> {
  children: ReactNode;
  noLeftMargin?: boolean;
  noTopMargin?: boolean;
}

export const GridContainer = ({
  children,
  noLeftMargin,
  noTopMargin,
  ...props
}: GridContainerProps) => {
  const modifiedProps: GridProps = {
    ...props,
    sx: {
      ...props.sx,
      ...(noLeftMargin && { marginLeft: '-8px !important' }),
      ...(noTopMargin && { marginTop: '0px !important' }),
    },
  };

  return (
    <Grid
      container
      direction='row'
      justifyContent='flex-start'
      alignItems='flex-end'
      spacing={1}
      rowSpacing={1}
      {...modifiedProps}
    >
      {children}
    </Grid>
  );
};

interface GridItemProps extends GridProps {
  children: ReactNode;
}

export const GridItem = ({ children, ...props }: GridItemProps) => {
  return (
    <Grid item {...props}>
      {children}
    </Grid>
  );
};

interface GridDividerProps {
  xs?: number;
}

export const GridDivider = ({ xs = 12 }: GridDividerProps) => (
  <Grid item xs={xs} sx={{ paddingTop: '0px !important' }} />
);

interface PaddedCardProps extends CardProps {
  children: ReactNode;
  padding?: number;
}

export const PaddedCard = ({
  children,
  sx,
  padding = 1,
  ...props
}: PaddedCardProps) => (
  <Card sx={{ height: '100%', padding: padding, ...sx }} {...props}>
    {children}
  </Card>
);

interface CardContainerProps {
  children: ReactNode;
  backgroundColor?: string;
  cardProps?: CardProps;
  cardContentProps?: any;
  noPadding?: boolean;
}

export const CardContainer = ({
  children,
  backgroundColor,
  noPadding = false,
  ...props
}: CardContainerProps) => {
  return (
    <Card
      variant={'outlined'}
      sx={{ ...(noPadding && { padding: 0 }), ...props.cardProps?.sx }}
      {...props.cardProps}
    >
      <CardContent
        sx={{
          padding: 1,
          '&:last-child': {
            paddingBottom: 1,
          },
          ...(backgroundColor && { backgroundColor: backgroundColor }),
        }}
        {...props.cardContentProps}
      >
        {children}
      </CardContent>
    </Card>
  );
};

interface PaddedTableCellProps extends TableCellProps {
  children: ReactNode;
  verticalAlign?: 'top' | 'center' | 'bottom';
}

export const PaddedTableCell = ({
  children,
  verticalAlign = 'center',
  ...props
}: PaddedTableCellProps) => {
  return (
    <TableCell
      style={{ padding: '8px 0 8px 8px', verticalAlign: verticalAlign }}
      {...props}
    >
      {children}
    </TableCell>
  );
};

export const PaddedTableCellNoBottomBorder = styled(PaddedTableCell)({
  borderBottom: 0,
});

interface LabeledTextProps {
  label?: string;
  text?: string | ReactNode;
  showEmptyValue?: boolean;
  sx?: any;
  labelSx?: any;
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
  title?: string;
}

export const LabeledText = ({
  label,
  text,
  showEmptyValue,
  sx,
  labelSx,
  align,
  title,
}: LabeledTextProps = {}) => {
  if (!text && !showEmptyValue) {
    return <></>;
  }

  return (
    <Stack title={title}>
      <Typography variant='caption' align={align} sx={labelSx}>
        {label}
      </Typography>
      {(typeof text === 'string' && (
        <Typography variant='body2' align={align} sx={sx}>
          {text || '-'}
        </Typography>
      )) ||
        text}
    </Stack>
  );
};

interface SmallTextProps {
  text?: string | number;
  variant?: 'caption' | 'body1' | 'body2';
  [key: string]: any;
}

export const SmallText = ({
  text,
  variant = 'caption',
  ...props
}: SmallTextProps) => {
  if (!text || !`${text}`.trim()) {
    return <></>;
  }

  return (
    <Typography variant={variant} {...props}>
      {text}
    </Typography>
  );
};

const defaultFontSize = 15;

export const colors = {
  highlightBlue: '#1965e0',
  lighterBlue: '#eefaff',

  highlightLightCyan: '#e8fff4',
  highlightDarkCyan: '#008B8B',

  highlightGreen: '#23c423',
  highlightLightGreen: '#ebfceb',
  green: '#b7eb8f',

  warningLow: '#ffdb59',
  warningMedium: '#ff8e3d',
  warningHigh: '#f31111',

  caption: '#627388',

  rowHighlight: '#ffdb59 !important',
  rowAlternativeBackground: '#f5f5f5',

  white: '#FFFFFF',
  lightWhite: 'rgba(255, 255, 255, 0.15)',

  lightGray: '#f0f0f0',
  lightGray2: '#DFDFDF',
  lightGray3: '#fafafa',
  // darkGray: 'darkgray',
  lightGray4: '#F1F1F2',
  mediumGray: '#D9D9D9',
  darkGray: '#63666A',
  grayForCard: '#A1A8AD',
  highlightLightGray: '#f2f2f2',

  purple: '#b5709a',

  orange: '#FF7F00',
  darkOrange: '#CC5200',
  highlightLightOrange: '#fff9e8',

  yellow: '#FFC91F',
  darkYellow: '#F2A700',

  blue: '#3171FF',
  darkBlue: '#162DB2',

  redGray: '#794b41',

  linkColor: '#0000A3',
  priority1: '#f76e6e',
  priority2: '#e49a50',
  priority3: '#f3df96',
  priority5: '#b9d9eb',
} as const;

// This object should not exposed. Colors should be managed by the theme and
// controlled by the "variant" and "color" props of MUI components.
// const colors = {
//   white: '#fff',
// };

export const defaultTheme = createTheme({
  palette: {
    primary: {
      main: colors.blue,
    },
    secondary: {
      main: colors.orange,
    },
  },
  components: {
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          border: `solid 1px ${colors.grayForCard}`,
          paddingLeft: '15px',
          paddingRight: '15px',
          borderRadius: '5px',
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        spacing: 0,
        rowSpacing: 0,
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          '&:not(.MuiInputBase-multiline)': {
            borderRadius: 999,
          },
          '& input:hover': {
            border: 'none',
            background: 'transparent',
          },
          '& input:focus': {
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            background: 'transparent',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          marginRight: '15px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            borderRadius: '999px',
            // padding: '4px 12px',
            minHeight: 36,
            backgroundColor: colors.lightWhite,
            '&:hover': {
              borderColor: colors.blue,
            },
            '&.Mui-focused': {
              borderColor: colors.darkOrange,
              borderWidth: 2,
            },
            '&.Mui-disabled': {
              backgroundColor: colors.lightGray,
              color: colors.darkGray,
              opacity: 1,
            },
            '&:before, &:after': {
              display: 'none',
            },
          },
        },
        popupIndicator: {
          color: colors.darkGray,
        },
        clearIndicator: {
          color: colors.darkGray,
        },
        tag: {
          borderRadius: '999px',
          padding: '0 8px',
          backgroundColor: colors.blue,
          color: colors.white,
          '& .MuiChip-deleteIcon': {
            color: colors.white,
            opacity: 0.7,
          },
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: colors.lighterBlue,
          },
          '&.Mui-focused': {
            backgroundColor: colors.lightWhite,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          '&.Mui-selected': {
            color: colors.darkOrange,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.darkOrange,
        },
      },
    },
  },
  typography: {
    fontSize: defaultFontSize,
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    body2: {
      fontSize: defaultFontSize - 1,
    },
    body1: {
      fontSize: defaultFontSize - 1,
    },
    caption: {
      fontSize: defaultFontSize - 4,
    },
    button: {
      fontSize: defaultFontSize,
      textTransform: 'initial',
    },
    standardCopy: {
      fontSize: '14px',
      color: colors.darkGray,
    },
    largeLink: {
      fontSize: '20px',
      color: colors.darkGray,
      textDecoration: 'underline',
      fontWeight: 'bold',
    },
    standardLink: {
      fontSize: '14px',
      color: colors.darkGray,
      textDecoration: 'underline',
    },
  },
});

export const legacyTheme = createTheme({
  components: { MuiCard: { defaultProps: { variant: 'outlined' } } },
  typography: {
    fontSize: defaultFontSize,
    display4: {
      fontSize: defaultFontSize,
    },
    display3: {
      fontSize: defaultFontSize,
    },
    display2: {
      fontSize: defaultFontSize,
    },
    display1: {
      fontSize: defaultFontSize,
    },
    headline: {
      fontSize: defaultFontSize,
    },
    title: {
      fontSize: defaultFontSize,
    },
    subheading: {
      fontSize: defaultFontSize,
    },
    body2: {
      fontSize: defaultFontSize - 1,
    },
    body1: {
      fontSize: defaultFontSize - 1,
    },
    caption: {
      fontSize: defaultFontSize - 4,
      color: colors.caption,
    },
    button: {
      fontSize: defaultFontSize,
      textTransform: 'initial',
    },
  } as any,
});

const baseButtonStyles = (buttonType?: string) => {
  const style =
    buttonType === 'error'
      ? {
          color: '#D32F2',
          borderColor: '#D32F2',
          fontWeight: 'bold',
          height: '32px',
          padding: '8px',
        }
      : {
          color: '#3D7CC8',
          borderColor: '#3D7CC8',
          fontWeight: 'bold',
          height: '32px',
          padding: '8px',
        };
  return style;
};

interface PamActionButtonProps extends Omit<LoadingButtonProps, 'type'> {
  text?: string;
  buttonType?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const PamActionButton = ({
  buttonType,
  text,
  ...props
}: PamActionButtonProps) => (
  <LoadingButton
    color={props.color || (buttonType === 'error' ? 'error' : 'primary')}
    variant={props.variant || 'outlined'}
    loading={props.loading || false}
    onClick={props.onClick || (() => {})}
    sx={
      props.sx
        ? { ...props.sx, ...baseButtonStyles(buttonType || '') }
        : {
            ...baseButtonStyles(buttonType || ''),
          }
    }
    {...props}
  >
    {text}
  </LoadingButton>
);

export const CompactTableCell = styled(TableCell)(() => {
  return {
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: colors.lightGray,
      color: colors.caption,
      fontSize: 12,
      padding: '0 10px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: '3px 10px',
    },
  };
});

export const CompactTableRow = styled(TableRow)(() => {
  return {
    '&:nth-of-type(even)': {
      backgroundColor: colors.rowAlternativeBackground,
    },
  };
});

interface TableTitleProps {
  title: string;
}

export const TableTitle = ({ title }: TableTitleProps) => (
  <Box
    sx={{
      backgroundColor: colors.lightGray2,
      width: '100%',
      padding: '5px 10px',
    }}
  >
    <Typography align='left' sx={{ fontWeight: 'bold' }}>
      {title}
    </Typography>
  </Box>
);

interface HighlightedTextProps {
  backgroundColor: string;
  text: string;
  bold?: boolean;
}

export const HighlightedText = ({
  backgroundColor,
  text,
  bold,
}: HighlightedTextProps) => (
  <Typography
    sx={{
      fontWeight: bold ? 'bold' : 'normal',
      borderRadius: '4px',
      padding: '0 3px',
      color: 'white',
      textAlign: 'center',
      backgroundColor: backgroundColor,
    }}
  >
    {text}
  </Typography>
);

interface ColoredLabelProps {
  sx?: any;
  children: ReactNode;
}

export const ColoredLabel = ({ sx, children }: ColoredLabelProps) => (
  <Typography
    sx={{
      fontSize: '11px',
      textAlign: 'center',
      padding: '2px 4px',
      borderRadius: '4px',
      margin: '2px 0',
      boxShadow: 'inset 0 0 0 1.5px rgb(1, 1, 1, 0.10)',
      color: 'white',
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const sizes = {
  small: { xl: 2, md: 4, sm: 6, xs: 12 },
  medium: { xl: 3, md: 6, xs: 12 },
  large: { xl: 4, md: 6, xs: 12 },
  larger: { xl: 6, xs: 12 },
} as const;

interface LoadingScreenProps {
  loading: boolean;
}

export const LoadingScreen = ({ loading }: LoadingScreenProps) => (
  <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
    <CircularProgress color='inherit' />
  </Backdrop>
);

interface FieldSetProps {
  disabled?: boolean;
  children: ReactNode;
}

export const FieldSet = ({ disabled, children }: FieldSetProps) => (
  <Box
    component='fieldset'
    sx={{ border: 'none', padding: 0 }}
    disabled={disabled}
  >
    {children}
  </Box>
);
